"use client";

import * as React from "react";
import { X, ArrowRight } from "lucide-react";

/**
 * "Tell us what to look at" — the questions that used to be signup step 2.
 *
 * A POPUP since 2026-11-04 (client: "this popup will have a close button and can be closed if they
 * want, but they'll be asked again when trying to navigate locked items"). It was an inline card on
 * the overview, which could not serve that second half at all — the overview is not where a locked
 * item is.
 *
 * 🔴 IT OWNS NO DECISION. Whether it is on screen, and what happens when it closes, belongs to
 * MembershipGate. The previous version took a "hold" on the upsell from in here and the ordering
 * did not work on the first load of a login, because a child's effects run before its parent's.
 * This component renders, saves, and reports — nothing else.
 *
 * Signup is one step now, so a new member arrives without preferences and the dashboard asks for
 * them instead. It NEVER blocks the dashboard: a member who ignores it keeps everything they came
 * for, and is asked again a few days later (the CRM holds the cadence, PROMPT_AFTER_DAYS).
 *
 * 🔴 Dismiss is not the same as answer. Closing this records only that we ASKED
 * (?prompted=1) — it must never stamp preferences as given, or the member is never asked again and
 * we never learn what they want. The two paths are separate calls on purpose.
 */
export function PreferencesModal({
  services,
  onSaved,
  onClose,
}: {
  /** The service options to offer, from the signup config so both places offer the same list. */
  services: { group: string; items: string[] }[];
  /** Answered. The gate stops asking and offers the upgrade. */
  onSaved: () => void;
  /** Closed without answering. The gate remembers the path so it does not reopen here. */
  onClose: () => void;
}) {
  const [picked, setPicked] = React.useState<string[]>([]);
  const [helpType, setHelpType] = React.useState<"market" | "new" | "">("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Escape closes it, like the sample-data disclaimer. The gate owns the scroll lock, so there is
  // none here — two components locking body overflow restore each other's value and leave the page
  // scrolling behind whichever modal is still open.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (s: string) =>
    setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  function dismiss() {
    onClose();
    // 🔴 Dismissing records only that we ASKED (?prompted=1) — never that preferences were given,
    // or the member is never asked again and we never learn what they want. Fire and forget: the
    // worst case if it fails is being asked again sooner.
    fetch("/api/member/preferences?prompted=1", { method: "POST" }).catch(() => {});
  }

  async function save() {
    if (!picked.length) {
      setError("Choose at least one service.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/member/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ services: picked, ...(helpType ? { helpType } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j?.error ?? "Couldn't save that just now.");
        return;
      }
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="fixed inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={dismiss} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="prefs-title"
        className="relative w-full max-w-lg bg-white rounded-2xl border border-lavender-200 shadow-2xl p-5 sm:p-6 my-auto"
      >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Not now"
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
      >
        <X className="w-4 h-4" />
      </button>

      <p id="prefs-title" className="font-bold text-navy-500 text-base">What should we look at for you?</p>
      <p className="text-[13px] text-gray-500 mt-1">Pick your services so we can find your savings.</p>

      <div className="mt-4 space-y-3">
        {services.map((g) => (
          <div key={g.group}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{g.group}</p>
            <div className="flex flex-wrap gap-2">
              {g.items.map((s) => {
                const on = picked.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggle(s)}
                    aria-pressed={on}
                    className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${
                      on
                        ? "bg-cj-800 text-white border-cj-800"
                        : "bg-white text-gray-600 border-gray-200 hover:border-cj-800"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {([["market", "Compare what I have"], ["new", "Set up something new"]] as const).map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setHelpType((h) => (h === v ? "" : v))}
            aria-pressed={helpType === v}
            className={`px-3 py-1.5 rounded-lg text-[13px] font-semibold border ${
              helpType === v ? "bg-navy-500 text-white border-navy-500" : "bg-white text-gray-600 border-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="text-xs text-red-500 mt-3">{error}</p>}

      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-cj-800 hover:bg-cj-900 disabled:opacity-60 text-white text-sm font-bold px-5 py-2.5"
        >
          {busy ? "Saving…" : <>Save <ArrowRight className="w-4 h-4" /></>}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 text-sm font-bold px-5 py-2.5"
        >
          Not now
        </button>
      </div>
    </div>
      </div>
  );
}
